import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { StandardEvents } from "@shopify/events";
export class MediaGallery extends Component {
  #eventRoot;
  #zoomDialog;
  #selection = 0;
  connectedCallback() {
    (super.connectedCallback(),
      (this.#eventRoot = this.closest(".shopify-section, dialog")),
      this.#eventRoot?.addEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect,
      ),
      (this.#zoomDialog = this.refs.zoomDialogComponent),
      this.#zoomDialog?.addEventListener(
        ThemeEvents.zoomMediaSelected,
        this.#handleZoomMediaSelected,
      ));
  }
  disconnectedCallback() {
    (this.#selection++,
      this.#eventRoot?.removeEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect,
      ),
      this.#zoomDialog?.removeEventListener(
        ThemeEvents.zoomMediaSelected,
        this.#handleZoomMediaSelected,
      ),
      (this.#eventRoot = void 0),
      (this.#zoomDialog = void 0),
      super.disconnectedCallback());
  }
  #findIncomingGallery(html) {
    if (this.id) {
      const match = html.getElementById(this.id);
      if (match?.matches("media-gallery")) return match;
    }
    const wrapper = this.closest("[data-tc-product-media]");
    if (wrapper?.id) {
      const match = html
        .getElementById(wrapper.id)
        ?.querySelector("media-gallery");
      if (match) return match;
    }
    const galleries = html.querySelectorAll("media-gallery");
    return galleries.length === 1 ? galleries[0] : null;
  }
  #slideRecords(gallery) {
    const slideshow = gallery.querySelector(
      'slideshow-component[ref="slideshow"]',
    );
    return slideshow
      ? [...slideshow.querySelectorAll('slideshow-slide[ref="slides[]"]')].map(
          (slide) => {
            const media = slide.querySelector("[data-media-id]"),
              id = media?.getAttribute("data-media-id"),
              image = media?.querySelector("img"),
              deferred = media?.querySelector("deferred-media, product-model"),
              template = deferred?.querySelector("template");
            return !id ||
              !image ||
              (deferred && !template) ||
              (!deferred && media.querySelector("video, iframe, model-viewer"))
              ? null
              : {
                  id,
                  signature: JSON.stringify([
                    image.getAttribute("src"),
                    image.getAttribute("srcset"),
                    image.getAttribute("alt"),
                    slide.hasAttribute("hidden"),
                    template?.innerHTML || "",
                  ]),
                };
          },
        )
      : [];
  }
  #selectVariantMedia(mediaId) {
    const slideshow = this.slideshow;
    if (
      mediaId == null ||
      this.presentation !== "carousel" ||
      !slideshow ||
      typeof slideshow.select !== "function"
    )
      return false;
    const slides = slideshow.slides || [
      ...slideshow.querySelectorAll('slideshow-slide[ref="slides[]"]'),
    ];
    const index = slides.findIndex(
      (slide) =>
        slide
          .querySelector("[data-media-id]")
          ?.getAttribute("data-media-id") === String(mediaId),
    );
    if (index < 0 || slides[index].hidden) return false;
    // Request only the selected image if it was lazy-loaded. Reuse its srcset
    // and responsive sizes instead of inserting a small thumbnail as the hero.
    const image = slides[index].querySelector(".product-media__image");
    if (image?.loading === "lazy") image.loading = "eager";
    if (slideshow.current !== index)
      slideshow.select(index, undefined, { animate: false });
    return true;
  }
  #syncCarousel(incoming, resource, locallySelectedMediaId = null) {
    if (
      this.presentation !== "carousel" ||
      incoming.dataset.presentation !== "carousel" ||
      this.getAttribute("class") !== incoming.getAttribute("class") ||
      this.getAttribute("style") !== incoming.getAttribute("style")
    )
      return !1;
    const slideshow = this.slideshow;
    if (!slideshow || typeof slideshow.select != "function") return !1;
    const current = this.#slideRecords(this),
      next = this.#slideRecords(incoming);
    if (
      !current.length ||
      current.length !== next.length ||
      current.some((item) => !item) ||
      next.some((item) => !item)
    )
      return !1;
    const byId = new Map(current.map((item) => [item.id, item.signature]));
    if (
      byId.size !== current.length ||
      new Set(next.map((item) => item.id)).size !== next.length ||
      next.some((item) => byId.get(item.id) !== item.signature)
    )
      return !1;
    const mediaId =
      resource?.featured_media?.id ?? resource?.featured_image?.id;
    if (mediaId == null) return !0;
    // The user may have browsed another image while the request was in flight.
    // A matching server response must not rewind an already-applied selection.
    if (
      locallySelectedMediaId != null &&
      String(mediaId) === String(locallySelectedMediaId)
    )
      return true;
    return this.#selectVariantMedia(mediaId);
  }
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest("product-card") ||
      event.target.closest(".shopify-section, dialog") !== this.#eventRoot ||
      !event.promise ||
      typeof event.promise.then != "function"
    )
      return;
    const picker = event.target.closest("variant-picker"),
      productId = picker?.dataset.productId,
      selection = ++this.#selection;
    const local = picker?.getLocalVariant?.();
    const locallySelectedMediaId =
      local && this.#selectVariantMedia(local.mediaId) ? local.mediaId : null;
    event.promise
      .then(({ detail } = {}) => {
        if (selection !== this.#selection || !this.isConnected || !detail?.html)
          return;
        const incoming = this.#findIncomingGallery(detail.html);
        !incoming ||
          (productId &&
            String(detail.productId) === String(productId) &&
            !detail.newProduct &&
            this.#syncCarousel(
              incoming,
              detail.resource,
              locallySelectedMediaId,
            )) ||
          this.replaceWith(incoming);
      })
      .catch((error) => {
        error?.name !== "AbortError" &&
          console.warn("[media-gallery] Event promise rejected:", error);
      });
  };
  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, void 0, { animate: !1 });
  };
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }
  preloadImage(index) {
    const media = this.refs.zoomDialogComponent?.refs.media[index];
    media && this.refs.zoomDialogComponent?.loadHighResolutionImage(media);
  }
  get slideshow() {
    return this.refs.slideshow;
  }
  get media() {
    return this.refs.media;
  }
  get presentation() {
    return this.dataset.presentation;
  }
}
customElements.get("media-gallery") ||
  customElements.define("media-gallery", MediaGallery);
